import React from 'react';
import PropTypes from 'prop-types';
import withSideEffect from 'react-side-effect';

import objectAssignDeep from 'utils/object-assign-deep';

const isServer = typeof window === 'undefined';

const HTTP_HOST = isServer ?
  process.env.HTTP_HOST :
  window.HTTP_HOST;

export function clone({children, ...source }) {
  return source ? JSON.parse(JSON.stringify(source)) : {};
}

/*
 * Takes a string and turns it into a hexidecimal(base 36) hash
 * for use as the react hashes
 */
export function stringHash(str) {
  let value = 5381;
  let i = str.length;

  while (i) {
    value = (value * 33) ^ str.charCodeAt(--i);
  }

  return (value >>> 0).toString(36);
}

/*
 * Construct the open graph tags based on the description / title / url prop if the open graph
 * versions aren't specified explicity in meta.property
 */
function ograph(prop) {
  if (!prop.meta) {
    prop.meta = {};
  }
  if (!prop.meta.property) {
    prop.meta.property = {};
  }

  const group = prop.meta.property;
  if (group) {
    if (prop.title && typeof prop.title === 'string' && !group['og:title']) {
      group['og:title'] = prop.title;
    }
    if (prop.hasOwnProperty('description') && typeof prop.description === 'string' && !group['og:description']) {
      group['og:description'] = prop.description;
    }
    if (prop.hasOwnProperty('canonical') && !group['og:url']) {
      group['og:url'] = `https://${HTTP_HOST}${prop.canonical}`;
    }
  }
  return prop;
}

/*
 * Construct the Twitter tags based on the description / title / url prop if the Twitter
 * versions aren't specified explicity in meta.name
 */
function twitter(prop) {
  if (!prop.meta) {
    prop.meta = {};
  }
  if (!prop.meta.name) {
    prop.meta.name = {};
  }

  const group = prop.meta.name;
  if (group) {
    if (prop.title && typeof prop.title === 'string' && !group['twitter:title']) {
      group['twitter:title'] = prop.title.substring(0, 70);
    }
    if (prop.hasOwnProperty('description') && typeof prop.description === 'string' && !group['twitter:description']) {
      group['twitter:description'] = prop.description.substring(0, 200);
    }
  }
  return prop;
}

/*
 * Filter out the unsupported hreflangs as defined by their exclusion from the hreflangs prop
 */
function updateHrefLang(props) {
  const {link} = props;
  if (link && link.rel && link.rel.alternate) {
    const alternates = link.rel.alternate;
    let {hrefLangs} = props;
    const languages = [];

    const enGbPresent = hrefLangs.indexOf('en-gb');
    // Get the language part of all of our hrefLangs
    hrefLangs.map((lang) => {
      // Push all our supported languages into the languages array
      if (languages.indexOf(lang.split('-')[0]) === -1) {
        // check if en-gb is present if not we dont want to push an en language version for now
        if (enGbPresent === -1 && lang.split('-')[0] === 'en') {
          return false;
        }
        // push the languages to our languages array
        languages.push(lang.split('-')[0]);
      }
      return false;
    });

    // concat the languages and hreflangs together
    hrefLangs = hrefLangs.concat(languages);
    // filter out our unsupported hregLangs
    const updatedHrefLangs = alternates.filter(item => (
      item.hrefLang === 'x-default' || hrefLangs.indexOf(item.hrefLang) !== -1
    ));
    // Update the link props
    props.link.rel.alternate = updatedHrefLangs;
  }

  return props;
}

/*
 * Filter out the unsupported hreflangs as defined by their exclusion from the hreflangs prop
 */
function maybeUpdateCanonical(props) {
  const {link} = props;
  if (link && link.rel && link.rel.canonical) {
    const {canonical} = link.rel;

    // If we haven't specified hrefLangs to exclude then dont worry
    if (!props.hrefLangs) return props;

    const {hrefLangs} = props;
    const lang = canonical.split('/') || [];

    // Check if our canonical is valid in our hrefLangs
    if (lang[3] && hrefLangs.indexOf(lang[3]) !== -1) {
      return props;
    }

    // Filter our hrefLangs for only primary region locales
    const primaryLocales = hrefLangs.filter(hLang => (
      hLang === 'en-gb' ||
      hLang === 'fr-fr'
    ));

    if (primaryLocales[0]) {
      // Update with our first primary locale as we should only have 1 locale when filtering
      props.link.rel.canonical = props.link.rel.canonical.replace(lang[3], primaryLocales[0]);
    }
  }

  return props;
}

/*
 * If you specify auto.ograph to true in the component we should try to automatically construct open
 * graph tags similarly If you specify auto.twitter to true in the component we should try to
 * automatically construct Twitter tags
 */
function autoProps(props) {
  if (props.auto.ograph === true) {
    ograph(props);
  }
  if (props.auto.twitter === true) {
    twitter(props);
  }

  return props;
}

/*
 * Handle the changes possibly made to description, viewport and canonical fields. Also triggers off
 * the auto prop generate for Twitter / Facebook
 */
function reducePropsTostate(propsList) {
  const props = {};

  for (const __props of propsList) {
    const _props = clone(__props);
    if (_props.hasOwnProperty('description')) {
      objectAssignDeep(_props, { meta: { name: { description: _props.description } } });
    }
    if (_props.hasOwnProperty('viewport')) {
      objectAssignDeep(_props, { meta: { name: { viewport: _props.viewport } } });
    }
    if (_props.hasOwnProperty('canonical')) {
      objectAssignDeep(_props, { link: { rel: { canonical: `https://${HTTP_HOST}${_props.canonical}` } } });
    }

    objectAssignDeep(props, _props);
  }

  if (props.hrefLangs) {
    updateHrefLang(props);
    maybeUpdateCanonical(props);
  }

  if (props.auto) {
    autoProps(props);
  }

  return props;
}

/*
 * takes a certain type of tag and returns an object including all of these tags with their
 * properties in an object.
 *
 * e.g. {
 *        content: "IE-edge"
 *        hash: "1fy4wau"
 *        httpEquiv: "X-UA-Compatible"
 *        tagName: "meta"
 *      }
 */
function parseTags(tagName, props = {}) {
  const tags = [];
  const contentKey = tagName === 'link' ? 'href' : 'content';
  Object.keys(props).forEach((groupKey) => {
    const group = props[groupKey];
    if (typeof group === 'string') {
      tags.push({
        tagName,
        [groupKey]: group,
      });
      return;
    }
    Object.keys(group).forEach((key) => {
      const values = Array.isArray(group[key]) ? group[key] : [group[key]];
      values.forEach((value) => {
        if (value === null) {
          return;
        }
        if (Object.prototype.toString.call(value) === '[object Object]') {
          tags.push({
            tagName,
            [groupKey]: key,
            ...value,
          });
        } else {
          tags.push({
            tagName,
            [groupKey]: key,
            [contentKey]: value,
          });
        }
      });
    });
  });
  tags.forEach((tag) => {
    tag.hash = stringHash(JSON.stringify(tag));
  });
  return tags;
}

/*
 * Gets the parsed tags for both meta and link elements utilising parseTags. Returns an array of the
 * parsed tags
 *
 */
function getTags(_props) {
  return [].concat(parseTags('meta', _props.meta), parseTags('link', _props.link));
}

/*
 * Insert our tags into the head element
 */
function insertDocumentMeta(props) {
  const tags = getTags(props);
  const nodes = Array.from(document.querySelectorAll('head [data-rdm]'));
  const hashes = nodes.map(node => node.getAttribute('data-rdm'));
  const newHashes = tags.map(tag => tag.hash);

  // if a node from the head of the page
  for (const node of nodes) {
    if (newHashes.indexOf(node.getAttribute('data-rdm')) > -1) continue; //eslint-disable-line no-continue
    node.parentNode.removeChild(node);
  }

  // get the head element
  const head = document.getElementsByTagName('head')[0];
  let lastNode = null;


  for (const tag of tags) {
    // Find our tag in our array of hashes
    const index = hashes.indexOf(tag.hash);
    if (index > -1) {
      lastNode = nodes[index];
      continue; //eslint-disable-line no-continue
    }

    const { tagName, hash, ...attr } = tag;
    const newNode = document.createElement(tagName);

    // set all our new tags attributes
    for (const prop of Object.keys(attr)) {
      newNode.setAttribute(prop, tag[prop]);
    }
    // set the tags hash
    newNode.setAttribute('data-rdm', hash);
    // insert the tag
    head.insertBefore(newNode, lastNode && lastNode.nextSibling);
    // keep a reference to last node we inserted
    lastNode = newNode;
  }
}

/*
 * trigger our meta insertion inside our side effect below only for client rendering not server
 */
function handleStateChangeOnClient(props) {
  insertDocumentMeta(props);
}

/*
 * Render our tags
 */
function render(meta = {}) {
  let i = 0;
  const tags = [];

  function renderTag(entry) {
    const { tagName, hash, ...attr } = entry;

    if (tagName === 'meta') {
      return (<meta {...attr} key={i++} data-rdm={hash} />);
    }
    if (tagName === 'link') {
      return (<link {...attr} key={i++} data-rdm={hash} />);
    }
    return null;
  }

  getTags(meta).reduce((acc, entry) => {
    tags.push(renderTag(entry));
    return tags;
  }, tags);

  return tags;
}

class DocumentMeta extends React.Component {
  static propTypes= {
    children: PropTypes.any,
    title: PropTypes.string,
    description: PropTypes.string,
    canonical: PropTypes.string,
    viewport: PropTypes.string,
    meta: PropTypes.objectOf(PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.objectOf(PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.string),
      ])),
    ])),
    link: PropTypes.objectOf(PropTypes.objectOf(PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          href: PropTypes.string.isRequired,
          hrefLang: PropTypes.string,
        }),
      ])),
    ]))),
    auto: PropTypes.objectOf(PropTypes.bool),
  };

  render() {
    const { children } = this.props;
    const count = React.Children.count(children);
    if (!count) return null;
    if (count === 1) return React.Children.only(children);
    return <div>{children}</div>;
  }
}

const DocumentMetaWithSideEffect = withSideEffect(
  reducePropsTostate,
  handleStateChangeOnClient,
)(DocumentMeta);

DocumentMetaWithSideEffect.renderAsReact = function rewindAsReact() {
  return render(DocumentMetaWithSideEffect.rewind());
};

if (typeof global !== 'undefined') {
  DocumentMetaWithSideEffect.renderAsHTML = function rewindAsHTML() {
    return require('react-dom/server') // eslint-disable-line global-require
      .renderToStaticMarkup(<div>{this.renderAsReact()}</div>)
      .replace(/(^<div>|<\/div>$)/g, '');
  };
}

export default DocumentMetaWithSideEffect;



// WEBPACK FOOTER //
// ./spa/Components/DocumentMeta.jsx